import React from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import PropTypes from 'prop-types';
import vahoy from '../../../javascript/vahoy';

class AiDetectorModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      thumbsClicked: false, // To track if thumbs up/down have been clicked
      progress: 0,
    };
  }

  componentWillUnmount() {
    this.stopProgress();
  }

  handleThumbsClick = (text, thumbType) => {
    // Handle the thumbs click and make them unclickable
    this.setState(() => ({
      thumbsClicked: true,
      clickedThumb: thumbType, // Track which thumb was clicked
    }));

    vahoy.track(`AiDetectorModal#${thumbType}`, { text, object_gid: this.props.ahoyObjectGid });
  };

  handleModalOpened = () => {
    if (this.props.loading) {
      this.setState({ progress: 0 });
      this.startProgress();
    }
  };

  handleModalClosed = () => {
    this.stopProgress();
    this.setState({ progress: 0, thumbsClicked: false, clickedThumb: undefined });
  };

  close = () => {
    this.props.onClose();
  };

  startProgress = () => {
    this.stopProgress();
    this.progressTimer = setInterval(() => {
      if (!this.props.loading) {
        this.setState({ progress: 100 });
        this.stopProgress();
        return;
      }
      this.setState((prev) => {
        const next = prev.progress + (Math.random() * 8 + 2);
        return { progress: Math.min(next, 90) };
      });
    }, 250);
  };

  stopProgress = () => {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  };

  render() {
    const {
            text, showModal, aiResult, loading, errorMessage,
          } = this.props;
    const {
            thumbsClicked, clickedThumb, progress,
          } = this.state;

    const colorMapping = {
      human: '#28a745',
      ai: '#d39e00',
    };

    const predictedClass = aiResult?.predictedClass || 'unknown';
    const classProbability = aiResult?.classProbability || {};
    const circleColor = colorMapping[predictedClass] || '#ccc';

    // Circle properties
    const circleRadius = 70; // Radius of the circle
    const circleCircumference = 2 * Math.PI * circleRadius; // Circumference of the circle

    const circleContainerStyle = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    };

    return (
      <Modal className="modal-lg" isOpen={showModal} toggle={this.close} onOpened={this.handleModalOpened} onClosed={this.handleModalClosed}>
        <ModalHeader toggle={this.close}>
          <div className="h3 mb-0">Check for AI</div>
        </ModalHeader>
        <ModalBody>
          {loading && (
            <div className="row ai-detector-response">
              <div className="col-12">
                <div className="mt-4 mb-2">
                  <div className="progress" style={{ height: '12px' }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${Math.round(progress)}%` }}
                      aria-valuenow={Math.round(progress)}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      aria-label="Scanning"
                    />
                  </div>
                </div>
                <div className="text-center mt-3 font-size-14px">
                  Scanning Pitch...
                </div>
              </div>
            </div>
          )}
          {!loading && errorMessage && (
            <div className="row ai-detector-response">
              <div className="col-12 text-center my-4">
                <i className="fa-solid fa-triangle-exclamation text-warning font-size-24px mb-2" />
                <div className="font-size-14px">{errorMessage}</div>
              </div>
            </div>
          )}
          {!loading && !errorMessage && (
            <div className="row ai-detector-response">
              <div className="col-md-6" style={circleContainerStyle}>
                <div style={{ position: 'relative', width: '160px', height: '160px' }}>
                  {/* SVG Progress Circle */}
                  <svg
                    width="160"
                    height="160"
                    viewBox="0 0 160 160"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                  >
                    {/* Foreground circle (human) */}
                    <circle
                      cx="80"
                      cy="80"
                      r={circleRadius}
                      stroke={colorMapping.human}
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${(classProbability.human || 0) * circleCircumference} ${circleCircumference}`}
                      strokeDashoffset="0"
                      strokeLinecap="butt"
                      style={{
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center',
                        transition: 'stroke-dashoffset 0.5s ease-in-out',
                      }}
                    />
                    {/* Foreground circle (AI) */}
                    <circle
                      cx="80"
                      cy="80"
                      r={circleRadius}
                      stroke={colorMapping.ai}
                      strokeWidth="10"
                      fill="none"
                      strokeDasharray={`${(classProbability.ai || 0) * circleCircumference} ${circleCircumference}`}
                      strokeDashoffset={`-${(classProbability.human || 0) * circleCircumference}`}
                      strokeLinecap="butt"
                      style={{
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center',
                        transition: 'stroke-dashoffset 0.5s ease-in-out',
                      }}
                    />
                  </svg>
                  {/* Text inside the circle */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ color: circleColor, fontWeight: 'bold', fontSize: '20px' }}>
                      {predictedClass.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <div className="percentage" style={{ color: colorMapping.human }}>
                    {Math.round((classProbability.human || 0) * 100)}% Human
                  </div>
                  <div className="percentage" style={{ color: colorMapping.ai }}>
                    {Math.round((classProbability.ai || 0) * 100)}% AI
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <span className="text-muted">How did Pangram do?</span>
                  <button
                    type="button"
                    onClick={() => this.handleThumbsClick(text, 'thumbsUp')}
                    disabled={thumbsClicked}
                    style={{ background: 'none', border: 'none', cursor: thumbsClicked ? 'not-allowed' : 'pointer' }}
                  >
                    <i
                      className="fa-solid fa-thumbs-up"
                      style={{ color: thumbsClicked && clickedThumb === 'thumbsUp' ? 'black' : 'gray' }}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => this.handleThumbsClick(text, 'thumbsDown')}
                    disabled={thumbsClicked}
                    style={{ background: 'none', border: 'none', cursor: thumbsClicked ? 'not-allowed' : 'pointer' }}
                  >
                    <i
                      className="fa-solid fa-thumbs-down"
                      style={{ color: thumbsClicked && clickedThumb === 'thumbsDown' ? 'black' : 'gray' }}
                    />
                  </button>
                </div>
              </div>
              <div className="col-md-6">
                <p className="text-muted">Powered by © Pangram</p>
                <p>
                  <a href="https://pangram.com/" className="text-primary" target="_blank" rel="noreferrer">Pangram</a> is the leading tool for spotting whether a document was written by AI like ChatGPT.
                </p>
                <p>
                  Note: Pangram is designed to provide accurate results on human and AI text.
                  It has a <a href="https://www.pangram.com/blog/all-about-false-positives-in-ai-detectors" target="_blank" rel="noreferrer">measured false positive rate</a> of 1 in 10,000.
                  We encourage you to use your own judgement when reviewing the results.
                </p>
                <p className="fst-italic">
                  <a href="https://www.qwoted.com/combating-disinformation-in-the-age-of-artificial-intelligence-2/" target="_blank" rel="noreferrer">Read more</a> about Qwoted's efforts to combat spam pitching and disinformation.
                </p>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }
}

AiDetectorModal.propTypes = {
  text: PropTypes.string.isRequired,
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  aiResult: PropTypes.shape({
    predictedClass: PropTypes.string,
    classProbability: PropTypes.shape({
      ai: PropTypes.number,
      human: PropTypes.number,
    }),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  ahoyObjectGid: PropTypes.string,
};

AiDetectorModal.defaultProps = {
  errorMessage: '',
  ahoyObjectGid: undefined,
};

export default AiDetectorModal;
